import { Colors } from "../../../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  AccountData,
  NotificationType,
  TeamMember,
} from "@markit/common.types";
import CreateTeamMemberModal from "./CreateTeamMemberModal";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { API } from "../../../API";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import TeamMemberDetailsModal from "./TeamMemberDetailsModal";

type ManageTeamMemberDropdownProps = {
  children: ReactNode;
  teamMember: TeamMember;
  isOpenDropdown: boolean;
  setIsOpenDropdown: (isOpen: boolean) => void;
  item?: AccountData; // If defined, the view option will be shown
};

const ManageTeamMemberDropdown = (props: ManageTeamMemberDropdownProps) => {
  const { children, teamMember, isOpenDropdown, setIsOpenDropdown, item } =
    props;
  const dispatch = useDispatch();
  const { uid } = useSelector(getAccountState).account.accountData;
  const [manageMemberModal, setManageMemberModal] = useState(false);
  const [deleteMemberModal, setDeleteMemberModal] = useState(false);
  const [viewMemberModal, setViewMemberModal] = useState(false);

  const dropdownItems: DropdownMenuItem[] = useMemo(() => {
    const menuItems: DropdownMenuItem[] = [];
    if (item) {
      menuItems.push({
        title: "View",
        icon: "ion:eye",
        key: "view_member",
        onPress: () => setViewMemberModal(true),
      });
    }
    menuItems.push(
      {
        title: "Edit",
        icon: "ion:pencil",
        key: "edit_member",
        onPress: () => setManageMemberModal(true),
      },
      {
        title: "Delete Member",
        icon: "ion:trash",
        key: "delete_member",
        onPress: () => setDeleteMemberModal(true),
        isDestructive: true,
      }
    );
    return menuItems;
  }, [item]);

  const deleteMemberOnPress = useCallback(async () => {
    await API.profile.deleteAccountTeamMember({
      uid,
      teamMemberUid: teamMember.uid,
    });
    showNotificationBanner(
      dispatch,
      "Member deleted",
      NotificationType.NEGATIVE
    );
  }, [uid, teamMember.uid, dispatch]);

  return (
    <>
      <DropdownMenu
        dropdownItems={dropdownItems}
        trigger={["click"]}
        placement="bottomRight"
        altWidth={item ? 150 : undefined}
        overlayStyle={{ paddingInline: item ? undefined : 60 }}
        isOpen={isOpenDropdown}
        onOpenChange={(isOpen: boolean) => setIsOpenDropdown(isOpen)}
      >
        {children}
      </DropdownMenu>
      {manageMemberModal ? (
        <CreateTeamMemberModal
          isVisible={manageMemberModal}
          setIsVisible={() => setManageMemberModal(false)}
          existingTeamMember={teamMember}
        />
      ) : null}
      {viewMemberModal && item ? (
        <TeamMemberDetailsModal
          isVisible={viewMemberModal}
          setIsVisible={() => setViewMemberModal(false)}
          teamMember={teamMember}
          item={item}
        />
      ) : null}
      <ConfirmDeleteModal
        heading="Delete Member"
        subtext="Are you sure you want to delete this member? This cannot be undone."
        deleteButtonText="Delete Member"
        icon={
          <Icon icon={"ion:trash"} height={35} style={{ color: Colors.RED3 }} />
        }
        hideModal={!deleteMemberModal}
        setIsVisible={setDeleteMemberModal}
        deleteOnPress={deleteMemberOnPress}
      />
    </>
  );
};

export default ManageTeamMemberDropdown;
